import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { toast } from "sonner";

// Admin-managed debug allowlist + payload log viewer.
//
// Accounts added here have their process-memory Gemini request + response +
// attachment manifest persisted on ai_call_logs. Remove an email and new
// calls stop getting logged (past rows stay until the retention cron wipes
// payload fields).

const PROJECT_REF = (() => {
  // Derive from VITE_SUPABASE_URL so Storage links work across envs.
  const url = import.meta.env.VITE_SUPABASE_URL ?? "";
  const match = url.match(/https?:\/\/([^.]+)\./);
  return match?.[1] ?? "";
})();

function storageUrl(bucket: string, path: string): string {
  if (!PROJECT_REF) return "";
  return `https://supabase.com/dashboard/project/${PROJECT_REF}/storage/buckets/${bucket}?path=${encodeURIComponent(path)}`;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

interface DebugAccount {
  email: string;
  added_by: string | null;
  added_at: string;
  notes: string | null;
}

interface AiCallLogRow {
  id: string;
  created_at: string;
  function_name: string;
  model: string;
  memory_id: string | null;
  user_id: string | null;
  locale: string | null;
  success: boolean;
  duration_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  error_message: string | null;
  request_text: string | null;
  response_text: string | null;
  attachments:
    | Array<{ bucket: string; path: string; mime: string; bytes: number }>
    | null;
}

export default function AiDebugDashboard() {
  const [accounts, setAccounts] = useState<DebugAccount[]>([]);
  const [logs, setLogs] = useState<AiCallLogRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [newEmail, setNewEmail] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [detailLog, setDetailLog] = useState<AiCallLogRow | null>(null);

  const loadAccounts = useCallback(async () => {
    const { data, error } = await supabase
      .from("ai_debug_accounts")
      .select("email, added_by, added_at, notes")
      .order("added_at", { ascending: false });
    if (error) {
      toast.error(error.message);
      return;
    }
    setAccounts((data ?? []) as DebugAccount[]);
  }, []);

  const loadLogs = useCallback(async () => {
    // Only show rows with payload — metadata-only logs belong on AI Costs.
    const { data, error } = await supabase
      .from("ai_call_logs")
      .select(
        "id, created_at, function_name, model, memory_id, user_id, locale, success, duration_ms, input_tokens, output_tokens, error_message, request_text, response_text, attachments",
      )
      .not("request_text", "is", null)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) {
      toast.error(error.message);
      return;
    }
    setLogs((data ?? []) as AiCallLogRow[]);
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadAccounts(), loadLogs()]).finally(() => setLoading(false));
  }, [loadAccounts, loadLogs]);

  const handleAdd = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!email) {
      toast.error("Email is required");
      return;
    }
    const { error } = await supabase.from("ai_debug_accounts").insert({
      email,
      notes: newNotes.trim() || null,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Tracking ${email}`);
    setNewEmail("");
    setNewNotes("");
    await loadAccounts();
  };

  const handleRemove = async (email: string) => {
    if (!window.confirm(`Stop tracking ${email}? Past logs stay until retention clears them.`)) {
      return;
    }
    const { error } = await supabase
      .from("ai_debug_accounts")
      .delete()
      .eq("email", email);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Stopped tracking ${email}`);
    await loadAccounts();
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">AI debug</h1>
        <p className="text-sm text-gray-500 mt-1">
          Emails on this list have their Gemini request + response + attachments
          persisted for admin audit. Only add accounts you own or have consent
          from — payloads contain family content.
        </p>
      </div>

      {/* ── Tracked accounts ────────────────────────────────────────────── */}
      <section className="mb-10 bg-white rounded-lg border border-gray-200 p-5">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Tracked accounts
        </h2>

        <div className="flex items-end gap-2 mb-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Email
            </label>
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="tester@example.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Notes (optional)
            </label>
            <input
              type="text"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              placeholder="QA account, my test family, …"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <button
            type="button"
            onClick={handleAdd}
            className="h-[38px] px-4 rounded-md bg-orange-600 text-white text-sm font-medium hover:bg-orange-700"
          >
            Track
          </button>
        </div>

        {accounts.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No accounts tracked.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                <th className="py-2">Email</th>
                <th className="py-2">Added</th>
                <th className="py-2">Notes</th>
                <th className="py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.email} className="border-b last:border-0">
                  <td className="py-2 font-mono text-xs">{a.email}</td>
                  <td className="py-2 text-gray-500">{timeAgo(a.added_at)}</td>
                  <td className="py-2 text-gray-600">{a.notes ?? "—"}</td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => handleRemove(a.email)}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Payload logs ────────────────────────────────────────────────── */}
      <section className="bg-white rounded-lg border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">
            Payload logs ({logs.length})
          </h2>
          <button
            type="button"
            onClick={() => void loadLogs()}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-500 italic">
            No logs yet. Add a tracked account and save a memory from it.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase tracking-wide border-b">
                <th className="py-2">When</th>
                <th className="py-2">Function</th>
                <th className="py-2">Model</th>
                <th className="py-2">Locale</th>
                <th className="py-2">Success</th>
                <th className="py-2">Tokens</th>
                <th className="py-2">Attach</th>
                <th className="py-2 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id} className="border-b last:border-0">
                  <td className="py-2 text-gray-500">{timeAgo(log.created_at)}</td>
                  <td className="py-2 font-mono text-xs">{log.function_name}</td>
                  <td className="py-2 font-mono text-xs">{log.model}</td>
                  <td className="py-2">{log.locale ?? "—"}</td>
                  <td className="py-2">
                    <span
                      className={`text-xs font-medium ${
                        log.success ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {log.success ? "ok" : "fail"}
                    </span>
                  </td>
                  <td className="py-2 text-gray-600">
                    {log.input_tokens ?? 0}/{log.output_tokens ?? 0}
                  </td>
                  <td className="py-2 text-gray-600">
                    {log.attachments?.length ?? 0}
                  </td>
                  <td className="py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setDetailLog(log)}
                      className="text-xs text-orange-600 hover:text-orange-800"
                    >
                      View payload
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* ── Payload detail sheet ────────────────────────────────────────── */}
      {detailLog && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex justify-end"
          onClick={() => setDetailLog(null)}
        >
          <div
            className="w-full max-w-3xl bg-white h-full overflow-auto p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Payload</h3>
              <button
                type="button"
                onClick={() => setDetailLog(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-6">
              <dt className="text-gray-500">When</dt>
              <dd className="text-gray-900">
                {new Date(detailLog.created_at).toLocaleString()}
              </dd>
              <dt className="text-gray-500">Function</dt>
              <dd className="font-mono text-xs">{detailLog.function_name}</dd>
              <dt className="text-gray-500">Model</dt>
              <dd className="font-mono text-xs">{detailLog.model}</dd>
              <dt className="text-gray-500">Locale</dt>
              <dd>{detailLog.locale ?? "—"}</dd>
              <dt className="text-gray-500">Memory</dt>
              <dd className="font-mono text-xs">{detailLog.memory_id ?? "—"}</dd>
              <dt className="text-gray-500">Success</dt>
              <dd>{detailLog.success ? "ok" : "fail"}</dd>
              <dt className="text-gray-500">Duration</dt>
              <dd>
                {detailLog.duration_ms ? `${detailLog.duration_ms}ms` : "—"}
              </dd>
              <dt className="text-gray-500">Tokens</dt>
              <dd>
                {detailLog.input_tokens ?? 0} in / {detailLog.output_tokens ?? 0} out
              </dd>
            </dl>

            {detailLog.error_message && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-red-700 mb-2">Error</h4>
                <pre className="bg-red-50 border border-red-200 rounded p-3 text-xs whitespace-pre-wrap overflow-auto">
                  {detailLog.error_message}
                </pre>
              </div>
            )}

            {detailLog.attachments && detailLog.attachments.length > 0 && (
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">
                  Attachments
                </h4>
                <ul className="text-sm space-y-1">
                  {detailLog.attachments.map((att, i) => (
                    <li
                      key={i}
                      className="flex items-center justify-between gap-2 bg-gray-50 border border-gray-200 rounded px-3 py-2"
                    >
                      <div className="flex-1 font-mono text-xs text-gray-700 truncate">
                        {att.bucket}/{att.path}
                      </div>
                      <div className="text-xs text-gray-500">
                        {att.mime} • {formatBytes(att.bytes)}
                      </div>
                      <a
                        href={storageUrl(att.bucket, att.path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-orange-600 hover:text-orange-800"
                      >
                        Open ↗
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Request text
              </h4>
              <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs whitespace-pre-wrap overflow-auto max-h-96">
                {detailLog.request_text ?? "—"}
              </pre>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                Response
              </h4>
              <pre className="bg-gray-50 border border-gray-200 rounded p-3 text-xs whitespace-pre-wrap overflow-auto max-h-96">
                {detailLog.response_text ?? "—"}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
